import { createBaseApi } from '../baseApi';
import { uploadFiles } from '../../../utils/uploadUtils';

// Create base auth API
const authApi = createBaseApi('authApi');

// Enhanced API with auth endpoints
export const enhancedAuthApi = authApi.injectEndpoints({
    endpoints: (builder) => ({
        // Get user profile including profile picture
        getUserProfile: builder.query({
            query: () => ({
                url: '/auth/profile',
                method: 'GET'
            }),
            providesTags: ['User'],
            transformResponse: (response) => {
                if (response?.success && response?.user) {
                    // Normalize profile picture
                    // Backend may return profilePic as:
                    //   - a populated file object { url: '...' }
                    //   - an array of file objects [{ url: '...' }]
                    //   - an unpopulated relation ID (number) — NOT usable
                    //   - null
                    // Fallback: profilePicUrl is a plain string URL (e.g. Google avatar)
                    let profilePic = null;
                    const rawPic = response.user.profilePic;

                    if (Array.isArray(rawPic) && rawPic.length > 0 && rawPic[0]?.url) {
                        profilePic = rawPic[0];
                    } else if (rawPic && typeof rawPic === 'object' && rawPic.url) {
                        profilePic = rawPic;
                    }

                    // Fallback to profilePicUrl (Google avatar URL stored as string)
                    if (!profilePic && response.user.profilePicUrl) {
                        profilePic = { url: response.user.profilePicUrl };
                    }

                    return {
                        ...response.user,
                        profilePic
                    };
                }
                return null;
            }
        }),

        // Update user profile (username, etc.)
        updateProfile: builder.mutation({
            query: (profileData) => ({
                url: '/profile/me',
                method: 'PUT',
                body: profileData
            }),
            invalidatesTags: ['User']
        }),

        // Update user avatar
        updateAvatar: builder.mutation({
            queryFn: async (file, { dispatch }, _extraOptions, baseQuery) => {
                try {
                    if (!file) throw new Error('No file provided');

                    const uploadedFiles = await uploadFiles([file]);
                    if (!uploadedFiles || uploadedFiles.length === 0) {
                        throw new Error('File upload failed');
                    }

                    const fileId = uploadedFiles[0].id;

                    const result = await baseQuery({
                        url: '/profile/me',
                        method: 'PUT',
                        body: { profilePic: fileId }
                    });

                    if (result.error) return { error: result.error };

                    // Invalidate tags to trigger refetch
                    dispatch(enhancedAuthApi.util.invalidateTags(['User']));
                    return { data: result.data };
                } catch (error) {
                    return { error: { message: error.message } };
                }
            },
            invalidatesTags: ['User']
        }),
        // Email/password login
        login: builder.mutation({
            query: (credentials) => ({
                url: '/auth/login',
                method: 'POST',
                body: credentials
            })
            // NOTE: Do NOT invalidate 'User' tag here.
            // The login handler calls window.location.reload() which re-fetches everything.
            // Invalidating here triggers an immediate /auth/profile fetch before the new
            // token is propagated, causing a 401 that clears the token from localStorage.
        }),

        // Email/password registration
        register: builder.mutation({
            query: (userData) => {
                // Attach the referral code a creator's link left behind, if any.
                let referralCode = null;
                try { referralCode = localStorage.getItem('pendingReferralCode'); } catch {}
                return {
                    url: '/auth/local/register',
                    method: 'POST',
                    body: referralCode && !userData.referralCode ? { ...userData, referralCode } : userData
                };
            }
            // Same as login — no invalidation; page reload handles it.
        }),

        // Social login endpoints
        getGoogleAuthUrl: builder.query({
            query: (params) => {
                const { return_to = '/my-closet', platform = 'susu_closet' } = params || {};
                const queryParams = new URLSearchParams({
                    return_to: return_to,
                    platform: platform
                });
                // Carry a creator's referral code through OAuth state so the
                // callback can credit them when it creates the user.
                try { const ref = localStorage.getItem('pendingReferralCode'); if (ref) queryParams.set('ref', ref); } catch {}

                return {
                    url: `/auth/social-auth-url/google?${queryParams.toString()}`,
                    method: 'GET'
                };
            }
        }),

        getAppleAuthUrl: builder.query({
            query: (params) => {
                const { return_to = '/my-closet', platform = 'susu_closet' } = params || {};
                const queryParams = new URLSearchParams({
                    return_to: return_to,
                    platform: platform
                });
                // Carry a creator's referral code through OAuth state so the
                // callback can credit them when it creates the user.
                try { const ref = localStorage.getItem('pendingReferralCode'); if (ref) queryParams.set('ref', ref); } catch {}

                return {
                    url: `/auth/social-auth-url/apple?${queryParams.toString()}`,
                    method: 'GET'
                };
            }
        }),

        nativeLogin: builder.mutation({
            query: (credentials) => ({
                url: '/auth/native-login',
                method: 'POST',
                body: credentials
            })
        }),

        // Delete user account
        deleteAccount: builder.mutation({
            query: () => ({
                url: '/profile/me',
                method: 'DELETE'
            }),
            invalidatesTags: ['User']
        }),
    })
});

export const {
    useGetUserProfileQuery,
    useUpdateProfileMutation,
    useUpdateAvatarMutation,
    useLoginMutation,
    useRegisterMutation,
    useGetGoogleAuthUrlQuery,
    useGetAppleAuthUrlQuery,
    useNativeLoginMutation,
    useDeleteAccountMutation
} = enhancedAuthApi;

export default enhancedAuthApi;
